import { Box, Button, Grid, Stack, TextField, Typography, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { MuiTelInput } from "mui-tel-input";
import { useState } from "react";
import { ModalComponent } from "@/pages/home/Components/Modal";
import { SplitButton } from "../SplitButton";
import styles from "../Leads.module.css"; 
import { LeadStageEnum, RegisterLead } from "@/interfaces/RegisterLead";
import { formatPhone } from '@/utils/formatphone';
import { findByPhone } from '@/services/api/client';

interface CreateLeadModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (leadData: RegisterLead) => void;
  loading?: boolean;
}

export function CreateLeadModal({
  open,
  onClose,
  onSave,
  loading = false,
}: CreateLeadModalProps) {
  const [leadData, setLeadData] = useState<RegisterLead>({
    name: "",
    phone: "+55",
    origin: "META",
    stage: LeadStageEnum.NAO_CLASSIFICADO,
    attendantId: "",
  });

  const origemOptions = ["LINK", "ADS", "META", "PRESENCIAL", "INDICAÇÃO", "EMAIL", "EVENTO", "ANÚNCIO", "YOUTUBE", "INSTAGRAM", "CAMPANHA", "INDICACAO DE CORRETORES"];
  const commonInputStyles = {
    top: 3,
    height: "20px",
    fontSize: "5px !important",
    color: "rgba(0, 0, 0, 0.54)",
    paddingRight: "9px !important",
    paddingLeft: "5px",
    backgroundColor: "white",
    lineHeight: "1rem",
  };

  const [errors, setErrors] = useState<{ phone?: string; name?: string }>({});
  const [phoneExists, setPhoneExists] = useState(false);
  const [clientFound, setClientFound] = useState<any>(null);
  const [openDialog, setOpenDialog] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setLeadData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    // Formata o telefone antes de salvar
    const formattedPhone = formatPhone(leadData.phone);
    
    // Validações de nome e telefone
    const nameValidationError = leadData.name.trim().length >= 2 ? undefined : "O nome deve ter pelo menos 2 caracteres.";
    const phoneValidationError = validarTelefone(formattedPhone) || await validarTelefoneAsync(formattedPhone);
  
    if (nameValidationError || phoneValidationError) {
      setErrors({ name: nameValidationError, phone: phoneValidationError });
      return;
    }
  
    // Salva o lead com o telefone formatado
    onSave({ ...leadData, phone: formattedPhone });
  };

  const handleClose = () => {
    setLeadData({
      name: "",
      phone: "+55",
      origin: "META",
      stage: LeadStageEnum.NAO_CLASSIFICADO,
      attendantId: "",
    });
    onClose();
  };
  
  const validarTelefone = (telefone: string) => {
    if (telefone.startsWith('+55')) {
      telefone = telefone.slice(3);
    }
    return telefone.length >= 10 ? undefined : "Digite um número válido.<br />Ex.: +55 (XX) XXXX-XXXX";
  };
  
  const validarTelefoneAsync = async (telefone: string) => {
    try {
      const response = await findByPhone(telefone);
  
      // Se a resposta indica que o cliente foi encontrado, definimos o erro de telefone duplicado.
      if (response?.data?.id) {
        setClientFound(response.data); // Salvando os dados do cliente encontrado
        setOpenDialog(true); // Exibe o diálogo com as informações do cliente
        setPhoneExists(true);
        return 'Número de telefone já cadastrado.'; // Indica que o telefone já existe
      }
  
      // Se não há cliente com o telefone informado, removemos qualquer erro de duplicidade
      setPhoneExists(false);
      return undefined; // Retorna indefinido, pois o telefone está disponível
  
    } catch (error: any) {
      // Se o erro for um 404, significa que o telefone está disponível, portanto, não é um erro
      if (error.status === 404) {
        setPhoneExists(false); // Define que o telefone está disponível
        return undefined; // Retorna indefinido para seguir com o cadastro
      }
  
      // Caso seja outro erro (diferente de 404), consideramos um problema de rede ou servidor
      console.error("Erro ao verificar o telefone:", error);
      return 'Erro ao verificar o telefone.'; // Mostra erro apenas para falhas reais
    }
  };  

  return (
    <ModalComponent open={open} onClose={handleClose}>
      <Box className={styles.modalHeader}>
        <Typography id="modal-title" variant="h6" component="h2">
          Novo Lead
        </Typography>
      </Box>
      <Box className={styles.modalBody}>
        <Stack spacing={2} className={styles.stack1}>
          <TextField
            label="Nome"
            name="name"
            value={leadData.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              handleInputChange(e);
              setErrors((prevErrors) => ({ ...prevErrors, name: undefined })); // Limpa o erro ao digitar
            }}
            onBlur={() => {
              if (leadData.name.trim().length < 2) {
                setErrors((prevErrors) => ({
                  ...prevErrors,
                  name: "O nome deve ter pelo menos 2 caracteres.",
                }));
              }
            }}
            error={!!errors.name}
            helperText={errors.name}
            InputLabelProps={{
              style: commonInputStyles,
            }}
            fullWidth
          />
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
          <MuiTelInput
            label="Telefone"
            name="phone"
            value={leadData.phone}
            error={!!errors.phone || phoneExists}
            helperText={
              errors.phone || phoneExists ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: errors.phone || "Número de telefone já existe."
                  }}
                />
              ) : undefined
            }
            onChange={(value) => {
              setLeadData((prevData) => ({ ...prevData, phone: value }));
              setErrors({ phone: undefined }); // Limpa erros ao digitar
              setPhoneExists(false); // Reseta a verificação de duplicidade
            }}
            onBlur={async () => {
              // Removemos a formatação personalizada para preservar a formatação do MuiTelInput
              const validationError = validarTelefone(leadData.phone);
              if (validationError) {
                setErrors({ phone: validationError });
              } else {
                setErrors({ phone: await validarTelefoneAsync(leadData.phone) });
              }
            }}
            InputLabelProps={{
              style: commonInputStyles,
            }}
            fullWidth
          />
          </Grid>
          <Grid item xs={12} md={6}>
            <SplitButton
              value={leadData.origin}
              options={origemOptions}
              className={styles.split}
              onChange={(newValue: string) => {
                setLeadData((prevData) => ({
                  ...prevData,
                  origin: newValue,
                }));
              }}
            />
          </Grid>
        </Grid>
      </Box>
      <Box className={styles.modalFooter}>
        <Button
          variant="contained"
          className={styles.buttonSaveClient}
          color="primary"
          onClick={handleSave}
          disabled={loading} 
        >
          {loading ? "Salvando..." : "Salvar"}
        </Button>
        <Button variant="outlined" onClick={handleClose}>
          Cancelar
        </Button>
      </Box>

      {/* Dialog para exibir informações do cliente existente */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Cliente já existente</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Já existe um cliente com o número de telefone "<strong>{clientFound?.telefone}</strong>".<br />
            <br />
            Cliente:{" "}
            {clientFound && (
              <a
                href={`/homepage?cliente=${clientFound.id}`} // Passa o clientId como query parameter
                target="_blank" // Abre em uma nova aba
                style={{ textDecoration: "underline" }}
              >
                <strong>{clientFound.nome}</strong>
              </a>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </ModalComponent>
  );
}
